import deepcopy from "deepcopy";
import moment from "moment";
import { ICvSubscriptionScheduleData, ICvSubscriptionScheduleDataDates, ICvSubscriptionScheduleDataStrings, ReportSubscription, FullSubscriptionSchedule, ReportSubscriptionSchedule } from "src/shared/models/subscriptions.model";
import { ClearViewUtils } from "../utils";
import { CvApiServiceBase } from "./cv-api-service-base";

export class SubscriptionSchedulerServiceBase {
    constructor(
        fromSharedService: boolean = false,
        private cvApiService?: CvApiServiceBase,
    ) {
        if (fromSharedService)
            return SubscriptionSchedulerServiceBase.getServiceBase();

        if (!cvApiService)
            this.cvApiService = CvApiServiceBase.getServiceBase();
    }

    static getServiceBase(): SubscriptionSchedulerServiceBase {
        return ClearViewUtils.getSharedService(SubscriptionSchedulerServiceBase, 'SubscriptionSchedulerServiceBase');
    }

    objectifyTheScheduleDates(schedule: ICvSubscriptionScheduleData) {
        //Due to inconsistencies in how CV's web API handles automatic conversions of "Javascript value to C# Datetime object"
        //  and the subsequent "assume it's UTC, convert to MST" action, we need to ensure that all DateTime values are objects.
        //  Date properties with object values receive the expected UTC conversion upon conversion to a C# DateTime object. 
        //  String values do not receive a UTC conversion.
        //
        //  TL;DR - Type safety: string bad, object good.
        //
        //Uses Moment.js to convert a UTC timestamp (ISO 8601) to a raw Javascript Date object, as Moment can convert UTC timestamps
        //  to raw Javascript Date objects.

        if (schedule == null)
            return null;

        //Expected non-array, non-embedded DateTime properties for schedules. Grouped in an array to reduce copy-pasted if statements
        ['endDate', 'EndDate', 'EndDateTime', 'firstExportTime', 'startDate', 'StartDate', 'StartDateTime'].forEach(function (prop) {
            if (schedule[prop] != undefined && typeof (schedule[prop]) == 'string') {
                schedule[prop] = moment(schedule[prop]).toDate();
            }
        });

        // Set the repeatEnd to today's date, to avoid time-zone drift issues when the subscription is saved
        if (schedule.repeatOptions != undefined && schedule.repeatOptions.repeatEnd != undefined && typeof (schedule.repeatOptions.repeatEnd) === 'string') {
            var repeatEnd = moment(schedule.repeatOptions.repeatEnd);
            var todayWithRepeatEnd = moment().hours(repeatEnd.hours()).minutes(repeatEnd.minutes());
            schedule.repeatOptions.repeatEnd = todayWithRepeatEnd.toDate();
        }

        //  Array based values
        ['extraExportTimes', 'ExtraExportTimes'].forEach(function (prop) {
            if (schedule[prop] != undefined) {
                var currentScopeHelper = schedule[prop]; //Because once you get too deep, "schedule" ceases to be!
                currentScopeHelper.forEach(function (value, index) {
                    if (typeof (value) == 'string') {
                        currentScopeHelper[index] = moment(value).toDate();
                    }
                });
            }
        });

        return schedule as ICvSubscriptionScheduleDataDates;
    }

    subscriptionDateTimesToStrings(schedule: ICvSubscriptionScheduleData) {
        // startDate
        if (schedule.startDate)
            schedule.startDate = moment(schedule.startDate).format("Y-MM-DD");
        else
            schedule.startDate = null; // Ensure other falsy values are explicitly null

        // endDate
        if (schedule.endDate)
            schedule.endDate = moment(schedule.endDate).format("Y-MM-DD");
        else
            schedule.endDate = null;

        // firstExportTime
        if (schedule.firstExportTime)
            schedule.firstExportTime = moment(schedule.firstExportTime).format("H:mm");
        else
            schedule.firstExportTime = null;

        // repeatOptions.repeatEnd
        if (schedule.repeatOptions && schedule.repeatOptions.repeatEnd)
            schedule.repeatOptions.repeatEnd = moment(schedule.repeatOptions.repeatEnd).format("H:mm");
        else if (schedule.repeatOptions)
            schedule.repeatOptions.repeatEnd = null;

        return schedule as ICvSubscriptionScheduleDataStrings;
    }

    isValidSchedule(inputSchedule: ICvSubscriptionScheduleData) {
        if (inputSchedule == null || typeof inputSchedule !== "object")
            return false;

        const schedule = this.objectifyTheScheduleDates(inputSchedule);
        try {
            if (schedule.exportType == null || schedule.exportType < 0 || schedule.exportType > 2)
                return false;
            if (schedule.frequency == null || schedule.frequency < 1 || schedule.frequency > 3)
                return false;
            if (schedule.reportType != 0 && schedule.reportType != 1)
                return false;
            if (!schedule.startDate || !schedule.firstExportTime)
                return false;

            // If endDate is specified, and its Date (w/o time) value is before the startDate, it's invalid.
            const endDate = schedule.endDate && new Date(schedule.endDate.getFullYear(), schedule.endDate.getMonth(), schedule.endDate.getDate());
            const startDate = endDate && new Date(schedule.startDate.getFullYear(), schedule.startDate.getMonth(), schedule.startDate.getDate());

            if (endDate != null && endDate < startDate)
                return false;
            if (schedule.repeatOptions.isRepeat) {
                if (typeof schedule.repeatOptions.repeatValue !== "number" || schedule.repeatOptions.repeatValue < 1)
                    return false;

                if (typeof schedule.repeatOptions.repeatType !== "number" || schedule.repeatOptions.repeatType < 0 || schedule.repeatOptions.repeatType > 2)
                    return false;

                const startTime = moment().hours(schedule.firstExportTime.getHours()).minutes(schedule.firstExportTime.getMinutes()).seconds(0);
                const endTime = moment().hours(schedule.repeatOptions.repeatEnd.getHours()).minutes(schedule.repeatOptions.repeatEnd.getMinutes()).seconds(0);
                if (endTime < startTime)
                    return false;

                let minutes: number = schedule.repeatOptions.repeatValue;

                let multiplier = 1;
                switch (schedule.repeatOptions.repeatType) {
                    case 0:
                        multiplier = 1;
                        break;
                    case 1:
                        multiplier = 60;
                        break;
                    case 2:
                        multiplier = 60 * 24;
                        break;
                    default:
                        multiplier = 0;
                }

                minutes *= multiplier;
                if (!(minutes >= 15)) { // any comparisons of NaN are false, so intentionally checking for NOT gte 15 to catch NaNs.
                    return false;
                }
            }
            // If the user has chosen not to send a subscription to themselves, and the other recipients list is empty, it's invalid.
            if (!schedule.sendToMe && schedule.recipients.length < 1)
                return false;
        } catch (ex) {
            return false;
        }

        return true;
    }

    async createSubscriptionSchedule(schedule: ICvSubscriptionScheduleData) {
        const stringSchedule = this.subscriptionDateTimesToStrings(deepcopy(schedule));
        const response = await this.cvApiService.post<ReportSubscription>('/api/v2/reports/subscriptions', stringSchedule);
        if (response.status_code == 200 || response.status_code == 201)
            return response;
        else
            throw response;
    }

    async createSubscriptionScheduleFromExistingModule(existingModuleKey: string, schedule: ICvSubscriptionScheduleData) {
        const stringSchedule = this.subscriptionDateTimesToStrings(deepcopy(schedule));
        const response = await this.cvApiService.post<ReportSubscription>(`/api/v2/reports/subscriptions/modulecopy/${existingModuleKey}`, stringSchedule);
        if (response.status_code == 200 || response.status_code == 201)
            return response.data;
        else
            throw response;
    }

    async getSubscriptionScheduleByRelatedKeyAndID(relatedKey: string, relatedID: number) {
        const response = await this.cvApiService.get<FullSubscriptionSchedule>(`/api/v2/reports/subscriptions/${relatedKey}/${relatedID}`);

        if (response.status_code == 200)
            return response.data;
        else
            throw response;
    }

    async getSubscriptionSchedule(subscriptionID: number, relatedID: number, relatedKey: string) {
        const response = await this.cvApiService.get<ReportSubscriptionSchedule>(`/api/v2/reports/subscriptions/${subscriptionID}/schedule/${relatedID}/${relatedKey}`);

        if (response.status_code == 200)
            return response.data;
        else
            throw response;
    }

    async getSubscriptionOwner() {
        const response = await this.cvApiService.get<string>('/api/v2/reports/subscriptions/owner');

        if (response.status_code == 200)
            return response.data;
        else
            throw response;
    }

    async updateSubscriptionSchedule(schedule: ICvSubscriptionScheduleData) {
        const stringSchedule = this.subscriptionDateTimesToStrings(deepcopy(schedule));
        const response = await this.cvApiService.put(`/api/v2/reports/subscriptions/${stringSchedule.subscriptionId}`, stringSchedule);
        if (response.status_code == 200) {
            return response;
        } else {
            throw response;
        }
    }

    async deleteSubscriptionSchedule(subscriptionId: number) {
        const response = await this.cvApiService.delete(`/api/v2/reports/subscriptions/${subscriptionId}`);

        if (response.status_code == 200)
            return response.data;
        else
            throw response;
    }
}
